class R13CartBundleOffer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.variants = this.parseJson('[data-r13-cart-bundle-variants]') || [];
    this.removeKeys = this.parseJson('[data-r13-cart-bundle-remove-keys]') || [];
    this.optionInputs = Array.from(this.querySelectorAll('[data-r13-cart-bundle-option]'));
    this.sharedSizeInput = this.querySelector('[data-r13-cart-bundle-shared-size]');
    this.sharedSizeTargets = Array.from(this.querySelectorAll('[data-r13-cart-bundle-shared-target]'));
    this.button = this.querySelector('[data-r13-cart-bundle-submit]');
    this.buttonText = this.button?.querySelector('span');
    this.submitLabel = this.button?.dataset.submitLabel || this.buttonText?.textContent || 'Add Complete Set';
    this.soldOutLabel = this.button?.dataset.soldOutLabel || 'Sold Out';
    this.incrementalBasePrice = Number(this.dataset.incrementalBasePrice || 0);
    this.spinner = this.button?.querySelector('.loading__spinner');
    this.price = this.querySelector('[data-r13-cart-bundle-price]');
    this.error = this.querySelector('[data-r13-cart-bundle-error]');
    this.cart = document.querySelector('cart-drawer');

    this.optionInputs.forEach((input) => input.addEventListener('change', (event) => {
      event.stopPropagation();
      this.syncSharedSizeOptions();
      this.updateVariant();
    }));
    this.button?.addEventListener('click', () => this.addBundle());
    this.syncSharedSizeOptions();
    this.updateVariant();
  }

  parseJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null');
    } catch (error) {
      console.warn('Unable to parse cart bundle data', error);
      return null;
    }
  }

  getVariant() {
    const optionIndexes = Array.from(new Set(this.optionInputs.map((input) => Number(input.dataset.r13CartBundleOption))));
    const selectedOptions = optionIndexes
      .sort((a, b) => a - b)
      .map((index) => this.querySelector(`[data-r13-cart-bundle-option="${index}"]:checked`)?.value
        || this.querySelector(`select[data-r13-cart-bundle-option="${index}"]`)?.value
        || this.querySelector(`input[type="hidden"][data-r13-cart-bundle-option="${index}"]`)?.value);
    return this.variants.find((variant) => (
      selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
    ));
  }

  syncSharedSizeOptions() {
    if (!this.sharedSizeInput || !this.sharedSizeTargets.length) return;

    this.sharedSizeTargets.forEach((input) => {
      input.value = this.sharedSizeInput.value;
    });
  }

  updateVariant() {
    this.syncSharedSizeOptions();
    const variant = this.getVariant();
    const available = Boolean(variant?.available);
    this.button.disabled = !available;
    this.buttonText.textContent = available ? this.submitLabel : this.soldOutLabel;
    if (variant && this.price) this.price.textContent = this.formatMoney(Math.max(0, variant.price - this.incrementalBasePrice));
  }

  async addBundle() {
    const variant = this.getVariant();
    if (!variant?.available || !this.button) return;

    this.setLoading(true);
    this.showError();

    try {
      const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];
      const addResponse = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variant.id,
          quantity: 1,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const addData = await addResponse.json();
      if (!addResponse.ok || addData.status) throw new Error(addData.description || addData.message || 'Unable to add the set.');

      let cartData = addData;
      if (this.removeKeys.length) {
        const updates = Object.fromEntries(this.removeKeys.map((key) => [key, 0]));
        const updateResponse = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
        });
        cartData = await updateResponse.json();
        if (!updateResponse.ok || cartData.status) throw new Error(cartData.description || cartData.message || 'Unable to update the cart.');
      }

      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'r13-cart-bundle-offer',
          productVariantId: variant.id,
          cartData,
        });
      }

      this.cart?.renderContents?.(cartData);
    } catch (error) {
      this.showError(error.message || 'Unable to add the set. Please try again.');
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (!this.button) return;
    this.button.disabled = loading;
    this.button.classList.toggle('loading', loading);
    this.spinner?.classList.toggle('hidden', !loading);
  }

  showError(message = '') {
    if (!this.error) return;
    this.error.hidden = !message;
    this.error.textContent = message;
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    const format = this.dataset.moneyFormat || '${{amount}}';
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString())
      .replace(/\{\{\s*amount\s*\}\}/, value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
  }
}

if (!customElements.get('r13-cart-bundle-offer')) {
  customElements.define('r13-cart-bundle-offer', R13CartBundleOffer);
}

if (!window.r13CartUpsellAddInitialized) {
  window.r13CartUpsellAddInitialized = true;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-r13-cart-upsell-add]');
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const cart = document.querySelector('cart-drawer');
    const spinner = button.querySelector('.loading__spinner');
    const variantId = Number(button.dataset.variantId);
    const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];

    button.disabled = true;
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const cartData = await response.json();
      if (!response.ok || cartData.status) throw new Error(cartData.description || cartData.message || 'Unable to add this item.');

      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'r13-cart-upsell',
          productVariantId: variantId,
          cartData,
        });
      }

      cart?.renderContents?.(cartData);
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  });
}
